import {
  AdditiveBlending,
  BoxGeometry,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DirectionalLight,
  DoubleSide,
  ShaderMaterial,
  Euler,
  Fog,
  Group,
  HemisphereLight,
  InstancedMesh,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  PMREMGenerator,
  PointLight,
  Points,
  PointsMaterial,
  Quaternion,
  Box3,
  Ray,
  Raycaster,
  Vector2,
  RepeatWrapping,
  Scene,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  ACESFilmicToneMapping,
  Vector3,
  WebGLRenderer,
  type Material,
  type Texture,
} from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { COUNT, GRID, STEP, buildModules, type ModuleSpec } from "./cubeModel";

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (a: number, b: number, v: number) => {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const hash = (n: number) => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/** choreography (scroll progress 0..1) — unchanged from the approved pass */
export const TIMELINE = { attractStart: 0.08, assembled: 0.68, textOut: [0.26, 0.54] as const, burstAt: 0.685 };

const FOV = 32;
const CAM_Z = 18;
const SEGS = 4; // short precision corner accents per module (face grooves removed)
const NP = 17; // points per bolt polyline (16 midpoint-displaced segments)
const POLY = 3; // main channel + 2 short branch filaments
const SPK = 5; // tiny contact sparks per discharge slot
const ARC_VS = `
attribute vec2 aData;
attribute float aLayer;
varying float vAcross;
varying float vAlpha;
varying float vLayer;
void main() {
  vAcross = aData.x;
  vAlpha = aData.y;
  vLayer = aLayer;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
const ARC_FS = `
varying float vAcross;
varying float vAlpha;
varying float vLayer;
void main() {
  float d = abs(vAcross);
  // layer 1: white-hot filament core wrapped in a blue/violet body
  float core = 1.0 - smoothstep(0.0, 0.32, d);
  float body = 1.0 - smoothstep(0.25, 1.0, d);
  vec3 bodyCol = vec3(0.5, 0.58, 1.0);
  vec3 hot = mix(bodyCol, vec3(1.0), core);
  float aCore = max(core, body * 0.75);
  // layer 0: soft localized glow
  float g = exp(-d * d * 4.0) * (1.0 - d);
  vec3 glowCol = vec3(0.34, 0.45, 1.0);
  vec3 col = mix(glowCol, hot, vLayer);
  float a = mix(g * 0.55, aCore, vLayer) * vAlpha;
  gl_FragColor = vec4(col * a, a);
}`;
const F = 0.503; // seam sits on the face, just proud of the bevel
const CORNERS = [
  [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
  [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1],
].map(([x, y, z]) => new Vector3(x * 0.502, y * 0.502, z * 0.502));
const TONE_COLOR = [new Color("#181c23"), new Color("#353b46"), new Color("#9aa3b2"), new Color("#1a2638"), new Color("#122033")];
const HALF_CUBE = ((GRID - 1) / 2) * STEP + 0.5;
const CUBE_SPAN = GRID * STEP + 0.6;

function glowTexture(ring: boolean) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const g = c.getContext("2d") as CanvasRenderingContext2D;
  const grad = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  if (ring) {
    grad.addColorStop(0, "rgba(255,255,255,0)");
    grad.addColorStop(0.72, "rgba(255,255,255,0)");
    grad.addColorStop(0.9, "rgba(255,255,255,1)");
    grad.addColorStop(0.95, "rgba(255,255,255,0.35)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
  } else {
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.25, "rgba(255,255,255,0.45)");
    grad.addColorStop(0.6, "rgba(255,255,255,0.08)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
  }
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 256);
  const tex = new CanvasTexture(c);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

/** fine directional streaks → brushed-titanium roughness variation */
function brushedTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const g = c.getContext("2d") as CanvasRenderingContext2D;
  g.fillStyle = "rgb(92,92,92)";
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 900; i += 1) {
    const y = hash(i * 1.3) * 256;
    const v = 60 + hash(i * 7.7) * 70;
    g.fillStyle = `rgba(${v},${v},${v},0.35)`;
    g.fillRect(hash(i * 3.1) * 256 - 60, y, 40 + hash(i * 5.9) * 200, 1);
  }
  const tex = new CanvasTexture(c);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  return tex;
}

/** dark studio with a large soft key box and thin cool strip lights → controlled metallic reflections */
function studioScene() {
  const sc = new Scene();
  const room = new Mesh(new BoxGeometry(40, 40, 40), new MeshBasicMaterial({ color: new Color("#030407"), side: BackSide }));
  sc.add(room);
  const panel = (w: number, h: number, col: string, k: number, pos: [number, number, number]) => {
    const m = new Mesh(new PlaneGeometry(w, h), new MeshBasicMaterial({ color: new Color(col).multiplyScalar(k) }));
    m.position.set(...pos);
    m.lookAt(0, 0, 0);
    sc.add(m);
  };
  panel(14, 8, "#e8eefc", 5.6, [-9, 11, 10]); // soft key
  panel(0.9, 16, "#9fd0ff", 8.5, [15, 2, -4]); // cool rim strip (narrower + hotter → crisper bevel glints)
  panel(10, 0.5, "#dbe8ff", 7, [-2, 14, -6]); // thin silver-blue top strip → edge specular on upper faces
  panel(18, 0.8, "#80bfff", 4, [0, -6, -15]); // back cyan strip
  panel(5, 5, "#ffffff", 1.6, [8, 6, 14]); // small fill card
  return sc;
}

interface Runtime {
  init: Vector3;
  ctrl: Vector3;
  target: Vector3;
  qInit: Quaternion;
}

interface Arc {
  a: Vector3;
  b: Vector3;
  I: number;
  seed: number;
  amp: number;
  branch: number;
}

export interface SceneOptions {
  still?: boolean;
  onLost?: () => void;
}

export class MagneticScene {
  private renderer: WebGLRenderer;
  private scene = new Scene();
  private camera = new PerspectiveCamera(FOV, 1, 0.1, 90);
  private group = new Group();
  private meshes: InstancedMesh[] = [];
  private mats: Material[] = [];
  private meshOf: InstancedMesh[] = [];
  private slotOf: number[] = [];
  private coreInner: InstancedMesh;
  private coreInnerMat: MeshBasicMaterial;
  private coreIds: number[] = [];
  private coreGlow: Sprite[] = [];
  private seamLocal: Vector3[][] = [];
  private seams: LineSegments;
  private bolts: Mesh;
  private slots: number;
  private boltPts: Vector3[][] = Array.from({ length: POLY }, () => Array.from({ length: NP }, () => new Vector3()));
  private flashes: Sprite[] = [];
  private bridgeEpoch: number[] = [];
  private bridgePair: [number, number][] = [];
  private tmpPts: Vector3[] = Array.from({ length: NP }, () => new Vector3());
  private zap: PointLight;
  private zapPos = new Vector3();
  private zapSparks: Points;
  private bx = new Vector3();
  private by = new Vector3();
  private bdir = new Vector3();
  private bs = new Vector3();
  private be = new Vector3();
  private camLocal = new Vector3();
  private halo: Sprite;
  private rings: Sprite[];
  private sparks: Points;
  private sparkDir: Vector3[] = [];
  private sparkSpeed: number[] = [];
  private glowTex: Texture;
  private ringTex: Texture;
  private brushTex: Texture;
  private mods: ModuleSpec[] = buildModules();
  private rt: Runtime[] = [];
  private lt: number[] = new Array(COUNT).fill(0);
  private qFinal = new Quaternion().setFromEuler(new Euler(0.5, -0.72, 0, "XYZ"));
  private pos: Vector3[] = Array.from({ length: COUNT }, () => new Vector3());
  private quat: Quaternion[] = Array.from({ length: COUNT }, () => new Quaternion());
  private arcPool: Arc[];
  private cornerDirs: Vector3[];
  private env: Texture;
  private pmrem: PMREMGenerator;
  private rim: PointLight;
  private flashLight: PointLight;
  private emissiveMats: MeshPhysicalMaterial[] = [];
  private maxArcs: number;
  private target = 0;
  private progress = 0;
  private armed = true;
  private burstStart = -1e9;
  private raf = 0;
  private last = 0;
  private visible = true;
  private still: boolean;
  private host: HTMLElement;
  private ro: ResizeObserver;
  private io: IntersectionObserver;
  private onLost?: () => void;
  private m4 = new Matrix4();
  private one = new Vector3(1, 1, 1);
  private coreScale = new Vector3(0.44, 0.44, 0.44);
  private tq = new Quaternion();
  private tq2 = new Quaternion();
  private te = new Euler();
  private tv = new Vector3();
  private tv2 = new Vector3();
  private d1 = new Vector3();
  private p1 = new Vector3();
  private p2 = new Vector3();
  private zAxis = new Vector3(0, 0, 1);
  // ---- Phase 05A pointer hover: temporary offsets layered on the scroll pose, rebuilt every frame
  private fine = false;
  private ptr = { x: 0, y: 0, on: false };
  private hoverW = new Float32Array(COUNT);
  private hoverT = new Float32Array(COUNT);
  private hoverDir: Vector3[] = Array.from({ length: COUNT }, () => new Vector3());
  private hScale = new Vector3();
  private hColor = new Color();
  private hdt = 0;
  // ---- Phase 05B final-cube drag: bounded yaw/pitch offsets composed over the scroll pose each frame
  private ray = new Raycaster();
  private ndc = new Vector2();
  private lray = new Ray();
  private inv = new Matrix4();
  private qFinalInv = new Matrix4();
  private cubeBox = new Box3(new Vector3(-HALF_CUBE, -HALF_CUBE, -HALF_CUBE), new Vector3(HALF_CUBE, HALF_CUBE, HALF_CUBE));
  private dq = new Quaternion();
  private de = new Euler(0, 0, 0, "YXZ");
  private drag = { state: 0 as 0 | 1 | 2, id: -1, sx: 0, sy: 0, tYaw: 0, tPitch: 0 };
  private yaw = 0;
  private pitch = 0;
  private ret = { on: false, t: 0, yaw: 0, pitch: 0 };
  private ptrTarget: Element | null = null;
  private cursorSet = "";
  private dGate = 0;
  private lastNow = 0;

  constructor(host: HTMLElement, opts: SceneOptions = {}) {
    this.host = host;
    this.still = Boolean(opts.still);
    this.onLost = opts.onLost;
    const mobile = window.innerWidth < 768;
    this.slots = mobile ? 4 : 7;
    this.maxArcs = this.slots + 8; // scroll-driven discharge slots + burst corner discharges
    this.arcPool = Array.from({ length: this.maxArcs }, () => ({ a: new Vector3(), b: new Vector3(), I: 0, seed: 0, amp: 0, branch: 0 }));

    this.renderer = new WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.3;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.domElement.className = "mo-canvas";
    this.renderer.domElement.setAttribute("aria-hidden", "true");
    this.renderer.domElement.addEventListener("webglcontextlost", this.handleLost);
    host.appendChild(this.renderer.domElement);

    this.pmrem = new PMREMGenerator(this.renderer);
    const studio = studioScene();
    this.env = this.pmrem.fromScene(studio, 0.02).texture;
    studio.traverse((o) => {
      const mesh = o as Mesh;
      if (mesh.isMesh) { mesh.geometry.dispose(); (mesh.material as Material).dispose(); }
    });
    this.scene.environment = this.env;
    this.scene.fog = new Fog(0x090c14, 14.5, 38); // slightly stronger depth falloff → near/far separation
    this.camera.position.set(0, 0, CAM_Z);

    // studio rig: one dominant soft key, cool rim from behind, almost no fill
    const key = new DirectionalLight(0xeef3ff, 4.2);
    key.position.set(-7, 9, 9);
    const rimDir = new DirectionalLight(0x80bfff, 3.9);
    rimDir.position.set(7, 3, -9);
    const fill = new HemisphereLight(0x8fa8d0, 0x0a0d14, 0.45);
    this.rim = new PointLight(0x80bfff, 50, 30, 2);
    this.flashLight = new PointLight(0xbfe0ff, 0, 30, 2);
    this.zap = new PointLight(0xa8b8ff, 0, 6, 2); // discharge light bouncing off nearby metal
    this.group.add(this.zap);
    this.scene.add(key, rimDir, fill, this.rim, this.flashLight, this.group);

    this.glowTex = glowTexture(false);
    this.ringTex = glowTexture(true);
    this.brushTex = brushedTexture();
    this.halo = new Sprite(new SpriteMaterial({ map: this.glowTex, color: 0x80bfff, blending: AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 }));
    this.halo.position.set(0, 0, -4);
    this.scene.add(this.halo);
    this.rings = [0, 1].map(() => {
      const sp = new Sprite(new SpriteMaterial({ map: this.ringTex, color: 0x9fd0ff, blending: AdditiveBlending, transparent: true, depthWrite: false, depthTest: false, opacity: 0 }));
      sp.renderOrder = 10;
      sp.visible = false;
      this.scene.add(sp);
      return sp;
    });

    // ---- materials: brushed titanium · polished metal · smoked glass · energy-core shell
    const geo = new RoundedBoxGeometry(1, 1, 1, 3, 0.04);
    const titanium = new MeshPhysicalMaterial({
      color: 0xffffff, metalness: 0.88, roughness: 0.3, roughnessMap: this.brushTex,
      anisotropy: 0.7, clearcoat: 0.5, clearcoatRoughness: 0.16, envMapIntensity: 1.9,
      emissive: new Color("#80bfff"), emissiveIntensity: 0,
    });
    const polished = new MeshPhysicalMaterial({
      color: 0xffffff, metalness: 1, roughness: 0.1, clearcoat: 1, clearcoatRoughness: 0.04, envMapIntensity: 2.3,
      emissive: new Color("#80bfff"), emissiveIntensity: 0,
    });
    const glass = new MeshPhysicalMaterial({
      color: 0xffffff, metalness: 0.15, roughness: 0.05, clearcoat: 1, clearcoatRoughness: 0.03,
      envMapIntensity: 1.8, transparent: true, opacity: 0.5, depthWrite: false, specularIntensity: 1,
    });
    const coreShell = new MeshPhysicalMaterial({
      color: 0xffffff, metalness: 0.3, roughness: 0.08, clearcoat: 1, clearcoatRoughness: 0.04,
      envMapIntensity: 1.6, transparent: true, opacity: 0.4, depthWrite: false,
      emissive: new Color("#4f9be8"), emissiveIntensity: 0.12,
    });
    this.emissiveMats = [titanium, polished, coreShell];
    const groups: { mat: MeshPhysicalMaterial; tones: number[]; order: number }[] = [
      { mat: titanium, tones: [0, 1], order: 0 },
      { mat: polished, tones: [2], order: 0 },
      { mat: glass, tones: [3], order: 3 },
      { mat: coreShell, tones: [4], order: 4 },
    ];
    groups.forEach(({ mat, tones, order }) => {
      const ids = this.mods.filter((m) => tones.includes(m.tone)).map((m) => m.id);
      const mesh = new InstancedMesh(geo, mat, Math.max(1, ids.length));
      mesh.count = ids.length;
      mesh.renderOrder = order;
      ids.forEach((id, i) => {
        this.meshOf[id] = mesh;
        this.slotOf[id] = i;
        mesh.setColorAt(i, TONE_COLOR[this.mods[id].tone]);
      });
      this.meshes.push(mesh);
      this.mats.push(mat);
      this.group.add(mesh);
    });
    // internal illumination of the energy cores, visible through their smoked shell
    this.coreIds = this.mods.filter((m) => m.tone === 4).map((m) => m.id);
    this.coreInnerMat = new MeshBasicMaterial({ color: new Color("#80bfff"), transparent: true, opacity: 0.9, depthWrite: false });
    this.coreInner = new InstancedMesh(geo, this.coreInnerMat, Math.max(1, this.coreIds.length));
    this.coreInner.count = this.coreIds.length;
    this.coreInner.renderOrder = 3;
    this.group.add(this.coreInner);
    this.coreGlow = this.coreIds.map(() => {
      const sp = new Sprite(new SpriteMaterial({ map: this.glowTex, color: 0x80bfff, blending: AdditiveBlending, transparent: true, depthWrite: false, opacity: 0.3 }));
      sp.renderOrder = 6;
      this.group.add(sp);
      return sp;
    });

    // ---- embedded luminous seams (face grooves + corner accents) instead of full wireframes
    this.mods.forEach((m) => {
      const rnd = (k: number) => hash(m.id * 17.13 + k * 3.7);
      const pts: Vector3[] = [];
      for (let k = 0; k < 4; k += 1) {
        const crn = CORNERS[Math.floor(rnd(k + 40) * 8)];
        const axis = Math.floor(rnd(k + 50) * 3);
        const p = new Vector3(crn.x, crn.y, crn.z).multiplyScalar(F / 0.502);
        const q = p.clone();
        q.setComponent(axis, p.getComponent(axis) * (1 - 0.62));
        pts.push(p, q);
      }
      this.seamLocal.push(pts);
    });
    const sGeoL = new BufferGeometry();
    sGeoL.setAttribute("position", new BufferAttribute(new Float32Array(COUNT * SEGS * 2 * 3), 3));
    sGeoL.setAttribute("color", new BufferAttribute(new Float32Array(COUNT * SEGS * 2 * 3), 3));
    this.seams = new LineSegments(sGeoL, new LineBasicMaterial({ vertexColors: true, transparent: true, blending: AdditiveBlending, depthWrite: false }));
    this.seams.frustumCulled = false;
    this.group.add(this.seams);

    // ---- electric discharge: camera-facing jagged ribbons (core/body + glow layers), one draw call
    const strips = this.maxArcs * POLY * 2;
    const vPer = NP * 2;
    const total = strips * vPer;
    const fGeo = new BufferGeometry();
    fGeo.setAttribute("position", new BufferAttribute(new Float32Array(total * 3), 3));
    const data = new Float32Array(total * 2);
    const layer = new Float32Array(total);
    const index: number[] = [];
    for (let st = 0; st < strips; st += 1) {
      for (let q = 0; q < NP; q += 1) {
        const v = st * vPer + q * 2;
        data[v * 2] = -1;
        data[(v + 1) * 2] = 1;
        layer[v] = st % 2;
        layer[v + 1] = st % 2;
        if (q < NP - 1) index.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
      }
    }
    fGeo.setAttribute("aData", new BufferAttribute(data, 2));
    fGeo.setAttribute("aLayer", new BufferAttribute(layer, 1));
    fGeo.setIndex(index);
    this.bolts = new Mesh(fGeo, new ShaderMaterial({
      vertexShader: ARC_VS, fragmentShader: ARC_FS, transparent: true, depthWrite: false,
      blending: AdditiveBlending, side: DoubleSide,
    }));
    this.bolts.frustumCulled = false;
    this.bolts.renderOrder = 5;
    this.group.add(this.bolts);

    // contact flashes (charge build-up + strike points)
    for (let k = 0; k < this.maxArcs * 2; k += 1) {
      const sp = new Sprite(new SpriteMaterial({ map: this.glowTex, color: 0xb4c4ff, blending: AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 }));
      sp.renderOrder = 6;
      sp.visible = false;
      this.flashes.push(sp);
      this.group.add(sp);
    }
    const zN = this.slots * SPK;
    const zGeo = new BufferGeometry();
    zGeo.setAttribute("position", new BufferAttribute(new Float32Array(zN * 3), 3));
    zGeo.setAttribute("color", new BufferAttribute(new Float32Array(zN * 3), 3));
    this.zapSparks = new Points(zGeo, new PointsMaterial({ map: this.glowTex, vertexColors: true, size: 0.09, transparent: true, blending: AdditiveBlending, depthWrite: false }));
    this.zapSparks.frustumCulled = false;
    this.group.add(this.zapSparks);

    const sparkN = mobile ? 70 : 150;
    for (let i = 0; i < sparkN; i += 1) {
      const y = 1 - (2 * (i + 0.5)) / sparkN;
      const rr = Math.sqrt(1 - y * y);
      const th = i * 2.399963;
      this.sparkDir.push(new Vector3(Math.cos(th) * rr, y, Math.sin(th) * rr));
      this.sparkSpeed.push(3 + hash(i * 3.1) * 7);
    }
    const spGeo = new BufferGeometry();
    spGeo.setAttribute("position", new BufferAttribute(new Float32Array(sparkN * 3), 3));
    spGeo.setAttribute("color", new BufferAttribute(new Float32Array(sparkN * 3), 3));
    this.sparks = new Points(spGeo, new PointsMaterial({ map: this.glowTex, vertexColors: true, size: 0.2, transparent: true, blending: AdditiveBlending, depthWrite: false }));
    this.sparks.frustumCulled = false;
    this.sparks.visible = false;
    this.group.add(this.sparks);

    this.cornerDirs = CORNERS.map((c) => c.clone().multiplyScalar(2 * HALF_CUBE).applyQuaternion(this.qFinal));

    this.layout();
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(host);
    this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; });
    this.io.observe(host);
    this.fine = !this.still && (window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false);
    if (this.fine) {
      this.qFinalInv.makeRotationFromQuaternion(this.qFinal.clone().invert());
      window.addEventListener("pointerdown", this.onDown);
      window.addEventListener("pointerup", this.onUp);
      window.addEventListener("pointercancel", this.onUp);
      window.addEventListener("pointermove", this.onPtr, { passive: true });
      document.documentElement.addEventListener("mouseleave", this.offPtr);
      window.addEventListener("blur", this.offPtr);
    }
    this.frame(performance.now());
  }

  private onPtr = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") { this.ptr.on = false; return; }
    this.ptr.x = e.clientX;
    this.ptr.y = e.clientY;
    this.ptr.on = true;
    this.ptrTarget = e.target as Element | null;
    const d = this.drag;
    if (d.state === 0 || e.pointerId !== d.id) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (d.state === 1 && dx * dx + dy * dy > 25) d.state = 2; // 5px threshold: plain clicks never rotate
    if (d.state === 2) {
      const lim = (v: number, m: number) => Math.max(-m, Math.min(m, v));
      d.tYaw = lim(dx * 0.0035, (25 * Math.PI) / 180);
      d.tPitch = lim(dy * 0.0025, (12 * Math.PI) / 180);
      this.setCursor("grabbing");
    }
  };

  private offPtr = () => { this.ptr.on = false; this.release(); };

  private static NO_DRAG = "a,button,input,textarea,select,label,summary,[role=button],[contenteditable],h1,h2,h3,p,nav,header";

  private onDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse" || e.button !== 0 || this.dGate < 1) return;
    if ((e.target as Element | null)?.closest?.(MagneticScene.NO_DRAG)) return;
    if (!this.hitCube(e.clientX, e.clientY)) return;
    e.preventDefault(); // stop text selection while grabbing the cube
    this.ret.on = false;
    this.drag = { state: 1, id: e.pointerId, sx: e.clientX, sy: e.clientY, tYaw: this.yaw, tPitch: this.pitch };
    // re-anchor so an in-flight return continues seamlessly from the current pose
    this.drag.sx -= this.yaw / 0.0035;
    this.drag.sy -= this.pitch / 0.0025;
  };

  private onUp = (e: PointerEvent) => {
    if (this.drag.state !== 0 && e.pointerId === this.drag.id) this.release();
  };

  private release() {
    if (this.drag.state === 0) return;
    this.drag.state = 0;
    this.drag.id = -1;
    this.ret = { on: true, t: 0, yaw: this.yaw, pitch: this.pitch };
    this.setCursor("");
  }

  private setCursor(c: string) {
    if (this.cursorSet === c) return;
    this.cursorSet = c;
    document.documentElement.style.cursor = c;
  }

  /** exact ray test against the assembled cube's oriented bounds (group space, un-rotated by qFinal) */
  private hitCube(cx: number, cy: number) {
    const rect = this.host.getBoundingClientRect();
    const x = cx - rect.left;
    const y = cy - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return false;
    this.ndc.set((x / rect.width) * 2 - 1, -(y / rect.height) * 2 + 1);
    this.ray.setFromCamera(this.ndc, this.camera);
    this.group.updateMatrixWorld();
    this.inv.copy(this.group.matrixWorld).invert().premultiply(this.qFinalInv);
    this.lray.copy(this.ray.ray).applyMatrix4(this.inv);
    return this.lray.intersectsBox(this.cubeBox);
  }

  /** drag allowed only once assembled and the burst flash has died down */
  private updateDrag(p: number, t: number, dt: number) {
    if (!this.fine) return;
    const flashDone = t - this.burstStart > 1.2;
    const gate = flashDone ? smooth(0.72, 0.76, p) : 0;
    this.dGate = gate;
    if (gate < 1 && this.drag.state !== 0) this.release(); // scroll takes priority
    if (this.drag.state === 2) {
      const k = 1 - Math.exp(-dt * 22);
      this.yaw += (this.drag.tYaw - this.yaw) * k;
      this.pitch += (this.drag.tPitch - this.pitch) * k;
    } else if (this.ret.on) {
      this.ret.t += dt;
      const u = clamp01((this.ret.t - 0.12) / 1.0); // brief hold, then ~1s ease-out
      const e = 1 - (1 - u) ** 3;
      this.yaw = this.ret.yaw * (1 - e);
      this.pitch = this.ret.pitch * (1 - e);
      if (u >= 1) { this.ret.on = false; this.yaw = 0; this.pitch = 0; }
    }
    if (this.drag.state === 0) {
      const over = gate >= 1 && this.ptr.on && !this.ptrTarget?.closest?.(MagneticScene.NO_DRAG) && this.hitCube(this.ptr.x, this.ptr.y);
      this.setCursor(over ? "grab" : "");
    }
  }

  /** hover only while dispersed; weight reaches 0 before assembly (p 0.10) begins */
  private hoverGate(p: number) {
    return this.fine ? 1 - smooth(0.06, 0.1, p) : 0;
  }

  /** projected hit test against real module positions (previous frame pose, 1-frame lag) */
  private pickHover(p: number, s: number) {
    this.hoverT.fill(0);
    const gate = this.hoverGate(p);
    if (gate <= 0 || !this.ptr.on) return;
    const rect = this.host.getBoundingClientRect();
    const mx = this.ptr.x - rect.left;
    const my = this.ptr.y - rect.top;
    if (mx < 0 || my < 0 || mx > rect.width || my > rect.height) return;
    this.group.updateMatrixWorld();
    const tanH = Math.tan((FOV * Math.PI) / 360);
    let hit = -1;
    let bestD = 1e9;
    for (let i = 0; i < COUNT; i += 1) {
      if (this.lt[i] > 0) continue;
      this.tv.copy(this.pos[i]).applyMatrix4(this.group.matrixWorld);
      const depth = this.camera.position.z - this.tv.z;
      if (depth <= 0.5) continue;
      const rad = ((s * 0.58) / (depth * tanH)) * (rect.height / 2);
      if (rad < 12) continue; // far background pieces stay inert
      this.tv.project(this.camera);
      const dx = (this.tv.x + 1) * 0.5 * rect.width - mx;
      const dy = (1 - this.tv.y) * 0.5 * rect.height - my;
      const d = Math.sqrt(dx * dx + dy * dy) / rad;
      if (d < 1.2 && d < bestD) { bestD = d; hit = i; }
    }
    if (hit < 0) return;
    this.camLocal.copy(this.camera.position);
    this.group.worldToLocal(this.camLocal);
    this.hoverT[hit] = gate;
    this.hoverDir[hit].subVectors(this.camLocal, this.pos[hit]).normalize().multiplyScalar(0.1);
    // up to two nearest neighbours drift a hair away from the hovered piece
    let n1 = -1; let n2 = -1; let d1 = 1e9; let d2 = 1e9;
    for (let i = 0; i < COUNT; i += 1) {
      if (i === hit || this.lt[i] > 0) continue;
      const d = this.pos[i].distanceTo(this.pos[hit]);
      if (d < d1) { d2 = d1; n2 = n1; d1 = d; n1 = i; } else if (d < d2) { d2 = d; n2 = i; }
    }
    [[n1, d1], [n2, d2]].forEach(([j, d]) => {
      if (j < 0 || d > 3.5) return;
      this.hoverT[j] = gate * 0.35 * (1 - d / 3.5);
      this.hoverDir[j].subVectors(this.pos[j], this.pos[hit]).normalize().multiplyScalar(0.05);
    });
  }

  setProgress(p: number) {
    this.target = clamp01(p);
    if (this.still) this.progress = this.target;
  }

  private layout() {
    const w = this.host.clientWidth || window.innerWidth;
    const h = this.host.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    const visH = 2 * Math.tan((FOV * Math.PI) / 360) * CAM_Z;
    const visW = visH * this.camera.aspect;
    const mode = w >= 1024 ? "d" : w >= 768 ? "t" : "m";
    const frac = mode === "d" ? 0.4 : mode === "t" ? 0.36 : 0.3;
    // module size scales with the viewport; the final 3x3x3 silhouette fills ~40% of hero height
    const s = Math.min((frac * visH) / CUBE_SPAN, ((mode === "m" ? 0.6 : 0.42) * visW) / CUBE_SPAN);
    this.group.position.set(0, 0, 0);
    this.group.scale.setScalar(s);
    this.rim.position.set(2.4 * s, 2.8 * s, -4);
    (this.sparks.material as PointsMaterial).size = 0.2 * s;
    (this.zapSparks.material as PointsMaterial).size = 0.09 * s;
    this.zap.distance = 7.5 * s;
    // on narrow screens pull the flanks in so foreground modules are never cropped
    const uK = mode === "m" ? 0.78 : mode === "t" ? 0.9 : 1;

    this.rt = this.mods.map((m) => {
      const target = new Vector3(...m.target).applyQuaternion(this.qFinal);
      const [u, v, z] = m.origin;
      const zz = mode === "m" ? Math.min(z, 2) : z;
      const k = (CAM_Z - zz) / CAM_Z;
      const init = new Vector3(((u - 0.5) * uK * visW * k) / s, ((0.5 - v) * visH * k) / s, zz / s);
      const qInit = new Quaternion().setFromEuler(new Euler(...m.rot));
      this.d1.subVectors(init, target);
      const ctrl = init.clone().lerp(target, 0.5).add(new Vector3(-this.d1.y * m.swirl, this.d1.x * m.swirl, m.lift));
      return { init, ctrl, target, qInit };
    });
    this.render(this.still ? 0 : performance.now() / 1000);
  }

  private frame = (now: number) => {
    this.raf = requestAnimationFrame(this.frame);
    const dt = Math.min(0.05, (now - (this.last || now)) / 1000);
    this.last = now;
    if (!this.visible || document.hidden) return;
    this.progress += (this.target - this.progress) * (1 - Math.exp(-dt * 7));
    if (Math.abs(this.target - this.progress) < 1e-4) this.progress = this.target;
    if (!this.still) {
      if (this.armed && this.progress >= TIMELINE.burstAt) {
        this.armed = false;
        this.burstStart = now / 1000;
      } else if (this.progress < 0.6) this.armed = true;
    }
    this.hdt = dt;
    this.lastNow = now / 1000;
    this.render(this.still ? 0 : now / 1000);
    this.hdt = 0;
  };

  private render(t: number) {
    const p = this.progress;
    const idleOn = this.still ? 0 : 1;
    const assembled = smooth(0.1, TIMELINE.assembled, p);
    const charge = smooth(0.62, 0.7, p);
    const age = t - this.burstStart;
    const burst = age < 0 || age > 3 ? 0 : age < 0.07 ? age / 0.07 : Math.exp(-(age - 0.07) * 2.1);

    this.camera.position.z = CAM_Z - 1.4 * assembled;
    this.group.rotation.set(
      Math.sin(t * 0.21) * 0.025 * idleOn,
      Math.sin(t * 0.13) * 0.05 * idleOn * (1 - assembled * 0.6) + (p - 0.68) * 0.9 * charge,
      0,
    );
    if (this.fine) {
      if (this.hdt > 0) this.updateDrag(p, this.lastNow, this.hdt);
      // scroll gate scales the offset so scrolling back blends it to exactly zero
      const w = smooth(0.7, 0.74, p);
      const yw = this.yaw * w;
      const pt = this.pitch * w;
      if (yw !== 0 || pt !== 0) {
        this.de.set(pt, yw, 0);
        this.dq.setFromEuler(this.de);
        this.group.quaternion.premultiply(this.dq); // screen-axis offset over the fresh base pose
      }
    }

    const sPos = this.seams.geometry.getAttribute("position") as BufferAttribute;
    const sCol = this.seams.geometry.getAttribute("color") as BufferAttribute;
    const sa = sPos.array as Float32Array;
    const sc = sCol.array as Float32Array;
    const s = this.group.scale.x || 1;
    const waveR = age * 7;
    let coreSlot = 0;
    this.pickHover(p, s);
    const hGate = this.hoverGate(p);
    const hk = 1 - Math.exp(-this.hdt * 9);

    for (let i = 0; i < COUNT; i += 1) {
      const m = this.mods[i];
      const r = this.rt[i];
      const start = 0.1 + m.delay * 0.32;
      const lt = clamp01((p - start) / 0.26);
      this.lt[i] = lt;
      const e = ease(lt);
      const a = (1 - e) * (1 - e);
      const b = 2 * (1 - e) * e;
      const c = e * e;
      const out = this.pos[i].set(
        r.init.x * a + r.ctrl.x * b + r.target.x * c,
        r.init.y * a + r.ctrl.y * b + r.target.y * c,
        r.init.z * a + r.ctrl.z * b + r.target.z * c,
      );
      const amp = (0.17 / s) * (1 - e) * idleOn;
      out.x += Math.sin(t * 0.55 + m.phase) * amp;
      out.y += Math.sin(t * 0.7 + m.phase * 1.3) * amp * 1.2;
      out.z += Math.cos(t * 0.45 + m.phase) * amp * 0.7;

      const wob = 0.08 * (1 - e) * idleOn;
      this.te.set(Math.sin(t * 0.31 + m.phase) * wob, Math.cos(t * 0.27 + m.phase) * wob, 0);
      this.tq.setFromEuler(this.te);
      this.tq2.copy(r.qInit).multiply(this.tq);
      this.quat[i].slerpQuaternions(this.tq2, this.qFinal, smooth(0, 1, lt));

      // pointer hover offset: eased weight × hard scroll gate, applied on top of the fresh base pose
      this.hoverW[i] += (this.hoverT[i] - this.hoverW[i]) * hk;
      if (this.hoverW[i] < 1e-4) this.hoverW[i] = 0;
      const hw = this.hoverW[i] * hGate;
      if (hw > 0) {
        out.addScaledVector(this.hoverDir[i], hw);
        this.te.set(0.03 * hw, 0.045 * hw, 0); // ≈ 1.7° / 2.6° at full weight
        this.tq.setFromEuler(this.te);
        this.quat[i].multiply(this.tq);
      }
      this.hScale.setScalar(1 + 0.035 * hw);
      this.hColor.copy(TONE_COLOR[m.tone]).multiplyScalar(1 + 0.7 * hw);
      this.meshOf[i].setColorAt(this.slotOf[i], this.hColor);
      this.m4.compose(out, this.quat[i], this.hScale);
      this.meshOf[i].setMatrixAt(this.slotOf[i], this.m4);

      if (m.tone === 4) {
        this.m4.compose(out, this.quat[i], this.coreScale);
        this.coreInner.setMatrixAt(coreSlot, this.m4);
        const g = this.coreGlow[coreSlot];
        g.position.copy(out);
        g.scale.setScalar(2.2 + 1.4 * burst);
        (g.material as SpriteMaterial).opacity = (0.22 + 0.1 * Math.sin(t * 2.1 + m.phase)) * (1 - 0.4 * assembled) + 0.6 * burst;
        this.m4.compose(out, this.quat[i], this.hScale);
        coreSlot += 1;
      }

      // seams: mostly dim, energy units brighter; charge flows during transit, burst wave sweeps outward
      const shimmer = 0.5 + 0.5 * Math.sin(t * 0.9 + m.phase * 2);
      const pulse = m.tone === 4 ? 1.2 + 0.45 * Math.sin(t * 2.3 + m.phase) : 1;
      const baseI = (0.1 + m.glow * (0.55 + 0.35 * shimmer) + e * 0.15 + charge * 0.35) * pulse;
      const transit = lt > 0 && lt < 1 ? 4 * lt * (1 - lt) : 0;
      const pts = this.seamLocal[i];
      for (let k = 0; k < SEGS * 2; k += 1) {
        const v = this.tv.copy(pts[k]).applyMatrix4(this.m4);
        const o = (i * SEGS * 2 + k) * 3;
        sa[o] = v.x; sa[o + 1] = v.y; sa[o + 2] = v.z;
        const flow = transit * Math.max(0, Math.sin(t * 9 - (v.x + v.y + v.z) * 2.2 + m.phase)) * 1.2;
        const dr = v.length() - waveR;
        const wave = burst * Math.exp(-dr * dr * 1.6) * 3.2;
        const extra = flow + wave + burst * 0.9;
        const I = baseI + extra;
        const hot = Math.min(1, extra * 0.6);
        sc[o] = (0.45 + 0.55 * hot) * I;
        sc[o + 1] = (0.72 + 0.28 * hot) * I;
        sc[o + 2] = I;
      }
    }
    this.meshes.forEach((mesh) => {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    });
    this.coreInner.instanceMatrix.needsUpdate = true;
    this.coreInnerMat.color.setRGB(0.5, 0.75, 1).multiplyScalar(1 + 0.35 * Math.sin(t * 2.3) * idleOn + charge * 0.5 + burst * 2.5);
    sPos.needsUpdate = true;
    sCol.needsUpdate = true;

    this.updateDischarge(t, p, idleOn, burst, s);
    this.updateBurst(age, burst, charge, assembled, s);
    this.renderer.render(this.scene, this.camera);
  }

  private pushArc(n: number, a: Vector3, b: Vector3, I: number, seed: number, amp: number, branch: number) {
    if (n >= this.maxArcs || I < 0.02) return n;
    const arc = this.arcPool[n];
    arc.a.copy(a);
    arc.b.copy(b);
    arc.I = I;
    arc.seed = seed;
    arc.amp = amp;
    arc.branch = branch;
    return n + 1;
  }

  /** endpoints on real geometry: corner→corner across a gap, or along a shared seam once docked */
  private endpoints(i: number, j: number, seam: boolean) {
    let c1 = 0;
    let best = 1e9;
    for (let c = 0; c < 8; c += 1) {
      const d = this.tv.copy(CORNERS[c]).applyQuaternion(this.quat[i]).add(this.pos[i]).distanceToSquared(this.pos[j]);
      if (d < best) { best = d; c1 = c; }
    }
    this.p1.copy(CORNERS[c1]).applyQuaternion(this.quat[i]).add(this.pos[i]);
    if (seam) {
      // run along the edge of the facing face (flip one axis, keep the one nearest the neighbour)
      let bd = 1e9;
      for (let ax = 0; ax < 3; ax += 1) {
        this.tv2.copy(CORNERS[c1]);
        this.tv2.setComponent(ax, -this.tv2.getComponent(ax));
        this.tv2.applyQuaternion(this.quat[i]).add(this.pos[i]);
        const d = this.tv2.distanceToSquared(this.pos[j]);
        if (d < bd) { bd = d; this.p2.copy(this.tv2); }
      }
      return;
    }
    best = 1e9;
    for (let c = 0; c < 8; c += 1) {
      const d = this.tv.copy(CORNERS[c]).applyQuaternion(this.quat[j]).add(this.pos[j]).distanceToSquared(this.p1);
      if (d < best) { best = d; this.p2.copy(this.tv); }
    }
  }

  /** midpoint-displacement bolt: coarse shape fixed per event, fine detail re-rolls every flicker */
  private buildBolt(out: Vector3[], a: Vector3, b: Vector3, amp: number, seedC: number, seedF: number) {
    this.bdir.subVectors(b, a);
    const len = this.bdir.length() || 1;
    this.bdir.divideScalar(len);
    this.bx.crossVectors(this.bdir, this.zAxis);
    if (this.bx.lengthSq() < 1e-4) this.bx.set(1, 0, 0);
    this.bx.normalize();
    this.by.crossVectors(this.bdir, this.bx).normalize();
    out[0].copy(a);
    out[NP - 1].copy(b);
    for (let step = NP - 1; step > 1; step >>= 1) {
      const half = step >> 1;
      const sd = step >= 8 ? seedC : seedF;
      const r = amp * len * (step / (NP - 1)) ** 0.85;
      for (let i = 0; i + step < NP; i += step) {
        const m = i + half;
        out[m].copy(out[i]).add(out[i + step]).multiplyScalar(0.5)
          .addScaledVector(this.bx, (hash(sd + m * 7.13 + step * 1.7) - 0.5) * r)
          .addScaledVector(this.by, (hash(sd * 1.31 + m * 3.71 + step * 2.9) - 0.5) * r);
      }
    }
    return len;
  }

  /** camera-facing ribbon along a polyline; taper 0 = both ends, 1 = toward the tip */
  private writeStrip(strip: number, pts: Vector3[], w: number, alpha: number, taper: number, jit: number) {
    const pa = (this.bolts.geometry.getAttribute("position") as BufferAttribute).array as Float32Array;
    const da = (this.bolts.geometry.getAttribute("aData") as BufferAttribute).array as Float32Array;
    const base = strip * NP * 2;
    for (let q = 0; q < NP; q += 1) {
      const u = q / (NP - 1);
      const P = pts[q];
      this.d1.subVectors(pts[Math.min(NP - 1, q + 1)], pts[Math.max(0, q - 1)]);
      this.tv.subVectors(this.camLocal, P);
      this.tv2.crossVectors(this.d1, this.tv).normalize();
      const env = taper ? (1 - u) ** 0.7 : Math.max(0.25, Math.sin(Math.PI * u) ** 0.35);
      const ww = w * env * (0.8 + 0.4 * hash(jit + q * 4.7));
      const v = base + q * 2;
      for (let side = 0; side < 2; side += 1) {
        const sg = side === 0 ? -1 : 1;
        const o = (v + side) * 3;
        pa[o] = P.x + this.tv2.x * ww * sg;
        pa[o + 1] = P.y + this.tv2.y * ww * sg;
        pa[o + 2] = P.z + this.tv2.z * ww * sg;
        da[(v + side) * 2 + 1] = alpha * (taper ? env : 1);
      }
    }
  }

  private showFlash(fi: number, at: Vector3, op: number, size: number) {
    if (fi >= this.flashes.length || op < 0.02) return fi;
    const f = this.flashes[fi];
    f.visible = true;
    f.position.copy(at);
    f.scale.setScalar(size);
    (f.material as SpriteMaterial).opacity = Math.min(1, op);
    return fi + 1;
  }

  /**
   * Electric discharge events. Each slot fires on its own seeded period (never in sync);
   * which cubes it links, how often slots fire and how hard they hit are all read from scroll
   * progress, so scrolling back reproduces the same behaviour.
   * Event shape: charge builds at a corner → bolt strikes → branches flicker → rapid decay.
   */
  private updateDischarge(t: number, p: number, idleOn: number, burst: number, s: number) {
    this.group.updateMatrixWorld();
    this.camLocal.copy(this.camera.position);
    this.group.worldToLocal(this.camLocal);
    const flick = Math.floor(t * 26);
    let n = 0;
    let fi = 0;
    let best = 0;
    this.flashes.forEach((f) => { f.visible = false; });
    const zp = this.zapSparks.geometry.getAttribute("position") as BufferAttribute;
    const zc = this.zapSparks.geometry.getAttribute("color") as BufferAttribute;
    const zpa = zp.array as Float32Array;
    const zca = zc.array as Float32Array;
    zca.fill(0);

    if (idleOn) {
      // sustained bridges: exploded 2 · assembling up to 4 (3 on phones) · assembled 1–2 then fade
      const grow = smooth(0.12, 0.45, p);
      const maxA = Math.min(this.slots, this.slots >= 7 ? 4 : 3);
      const settle = smooth(0.62, 0.7, p);
      const active = (2 + (maxA - 2) * grow) * (1 - settle) + 2 * settle;
      const fade = 1 - smooth(0.7, 0.8, p);
      const energy = (0.62 + 0.4 * grow + 0.3 * smooth(0.55, 0.68, p)) * fade;
      for (let k = 0; k < maxA; k += 1) {
        const gate = clamp01(active - k);
        if (gate <= 0 || energy <= 0.01) continue;
        // each bridge lives several seconds on its own offset clock, with soft ignite / release
        const L = 4.2 + hash(k * 5.3 + 1) * 2.6;
        const tt = t + hash(k * 9.7 + 2) * L;
        const e = Math.floor(tt / L);
        const local = (tt - e * L) / L;
        const life = smooth(0, 0.08, local) * (1 - smooth(0.9, 1, local));
        const seed = k * 97 + e * 13;
        if (this.bridgeEpoch[k] !== e || !this.bridgePair[k]) {
          // pick a pair once per bridge life so the connection stays put
          const i = Math.floor(hash(seed * 1.91) * COUNT) % COUNT;
          let j = -1;
          let d1 = 1e9;
          for (let m = 0; m < COUNT; m += 1) {
            if (m === i) continue;
            const d = this.pos[m].distanceToSquared(this.pos[i]);
            if (d < d1) { d1 = d; j = m; }
          }
          this.bridgeEpoch[k] = e;
          this.bridgePair[k] = [i, j];
        }
        const [i, j] = this.bridgePair[k];
        const dist = this.pos[i].distanceTo(this.pos[j]);
        const reach = 1 - smooth(6, 9, dist);
        if (reach <= 0) continue;
        const seam = dist < STEP * 1.3;
        this.endpoints(i, j, seam);
        // steady body with breathing pulse + fast fine flicker, occasional surge
        const breathe = 0.82 + 0.12 * Math.sin(t * 5.3 + k * 2.1) + 0.06 * Math.sin(t * 11.7 + k);
        const flickV = 0.88 + 0.12 * hash(seed + flick * 3.1);
        const win = Math.floor(t * 2.2 + k * 0.37);
        const wl = t * 2.2 + k * 0.37 - win;
        const surgeOn = hash(seed + win * 5.7) > 0.55;
        const spike = surgeOn ? Math.max(0, 1 - wl / 0.28) : 0;
        const I = energy * reach * gate * life * breathe * flickV * (seam ? 0.75 : 1) * (1 + 0.45 * spike);
        // branches only during brief surges
        n = this.pushArc(n, this.p1, this.p2, I, seed, seam ? 0.06 : 0.13, spike);
        const glowI = I * (0.45 + 0.5 * spike);
        fi = this.showFlash(fi, this.p1, glowI, 0.32 + 0.35 * glowI);
        fi = this.showFlash(fi, this.p2, glowI * 0.8, 0.28 + 0.3 * glowI);
        if (I > best) { best = I; this.zapPos.addVectors(this.p1, this.p2).multiplyScalar(0.5); }
        // tiny contact sparks only on surges
        const age = wl / 2.2;
        for (let q = 0; q < SPK; q += 1) {
          const o = (k * SPK + q) * 3;
          const h = seed * 3.3 + win * 1.9 + q * 11.7;
          const sp = 1.4 + hash(h) * 2.2;
          zpa[o] = this.p2.x + (hash(h + 1) - 0.5) * 2 * sp * age;
          zpa[o + 1] = this.p2.y + (hash(h + 2) - 0.5) * 2 * sp * age - 3 * age * age;
          zpa[o + 2] = this.p2.z + (hash(h + 3) - 0.5) * 2 * sp * age;
          const f = spike * spike * Math.min(1, I) * (seam ? 0.4 : 1);
          zca[o] = 0.75 * f; zca[o + 1] = 0.82 * f; zca[o + 2] = 1.1 * f;
        }
      }
    }
    zp.needsUpdate = true;
    zc.needsUpdate = true;

    // approved burst discharge from the 8 corners
    if (burst > 0.03) {
      const tick = Math.floor(t * 9);
      for (let k = 0; k < 8; k += 1) {
        const crn = this.cornerDirs[k];
        this.p1.copy(crn).multiplyScalar(0.5);
        this.p2.copy(crn).multiplyScalar(0.5 * (1.4 + 0.6 * hash(k * 7 + tick)));
        n = this.pushArc(n, this.p1, this.p2, burst * 1.5, k * 31 + tick, 0.24, 1);
        fi = this.showFlash(fi, this.p1, burst * 0.8, 0.8);
      }
    }
    this.zap.position.copy(this.zapPos);
    this.zap.intensity = best * 26 * s * s * 5.2; // discharge lights nearby metal a touch more

    // render bolts: main channel + up to 2 branches, each as glow + core ribbons
    const da = (this.bolts.geometry.getAttribute("aData") as BufferAttribute).array as Float32Array;
    for (let v = 0; v < da.length / 2; v += 1) da[v * 2 + 1] = 0;
    for (let k = 0; k < n; k += 1) {
      const arc = this.arcPool[k];
      const main = this.boltPts[0];
      // slow shape drift: blend between two coarse shapes, fine jag re-rolls each flicker
      const wt = t * 1.6 + k * 0.53;
      const w0 = Math.floor(wt);
      const wf = smooth(0, 1, wt - w0);
      const len = this.buildBolt(main, arc.a, arc.b, arc.amp, arc.seed + w0 * 3.3, arc.seed + flick * 17.3);
      this.buildBolt(this.tmpPts, arc.a, arc.b, arc.amp, arc.seed + (w0 + 1) * 3.3, arc.seed + flick * 17.3);
      for (let q = 0; q < NP; q += 1) main[q].lerp(this.tmpPts[q], wf);
      const I = arc.I;
      const base = k * POLY * 2;
      this.writeStrip(base, main, 0.2, Math.min(1, I) * 0.8, 0, flick + k);
      this.writeStrip(base + 1, main, 0.032, Math.min(1.2, I * 1.1), 0, flick * 3 + k);
      if (arc.branch <= 0.02) continue;
      for (let b = 1; b < POLY; b += 1) {
        const bSeed = arc.seed * 1.7 + b * 23 + Math.floor(flick / 3) * 5;
        if (hash(bSeed) < 0.25) continue;
        const si = 3 + Math.floor(hash(bSeed + 1) * 9);
        this.bs.copy(main[si]);
        this.be.subVectors(main[si + 2], main[si]).normalize().multiplyScalar(len * (0.18 + 0.16 * hash(bSeed + 2)))
          .addScaledVector(this.by, (hash(bSeed + 3) - 0.5) * len * 0.35)
          .addScaledVector(this.bx, (hash(bSeed + 4) - 0.5) * len * 0.25)
          .add(this.bs);
        const bpts = this.boltPts[b];
        this.buildBolt(bpts, this.bs, this.be, 0.22, bSeed, bSeed + flick * 7.1);
        const bi = I * arc.branch * 0.65;
        this.writeStrip(base + b * 2, bpts, 0.12, Math.min(1, bi) * 0.7, 1, flick + b);
        this.writeStrip(base + b * 2 + 1, bpts, 0.02, Math.min(1, bi), 1, flick * 5 + b);
      }
    }
    this.bolts.geometry.getAttribute("position").needsUpdate = true;
    this.bolts.geometry.getAttribute("aData").needsUpdate = true;
  }

  private updateBurst(age: number, burst: number, charge: number, assembled: number, s: number) {
    this.renderer.toneMappingExposure = 1.3 + burst * 0.45;
    this.emissiveMats[0].emissiveIntensity = 0.015 * charge + burst * 0.3;
    this.emissiveMats[1].emissiveIntensity = 0.02 * charge + burst * 0.35;
    this.emissiveMats[2].emissiveIntensity = 0.12 + 0.3 * charge + burst * 1.2;
    this.rim.intensity = 80 + 110 * assembled + 140 * charge + 650 * burst;
    this.flashLight.position.set(0, 0, 6 * s);
    this.flashLight.intensity = 480 * burst;

    const hm = this.halo.material as SpriteMaterial;
    hm.opacity = Math.min(1, 0.04 + 0.2 * assembled + 0.22 * charge + 0.9 * burst);
    this.halo.scale.setScalar(s * (6.5 + 1.5 * charge + 5 * burst));

    this.rings.forEach((ring, k) => {
      const ak = age - k * 0.2;
      const on = ak > 0 && ak < 1.5;
      ring.visible = on;
      if (!on) return;
      ring.scale.setScalar(s * (5 + 26 * (1 - Math.exp(-ak * 2.3))));
      (ring.material as SpriteMaterial).opacity = (1 - ak / 1.5) ** 2 * (k === 0 ? 0.95 : 0.55);
    });

    const life = 1.8;
    this.sparks.visible = age > 0 && age < life;
    if (this.sparks.visible) {
      const sp = this.sparks.geometry.getAttribute("position") as BufferAttribute;
      const sc = this.sparks.geometry.getAttribute("color") as BufferAttribute;
      const pa = sp.array as Float32Array;
      const pc = sc.array as Float32Array;
      const travel = (1 - Math.exp(-age * 2.4)) / 2.4;
      this.sparkDir.forEach((d, i) => {
        const fade = Math.max(0, 1 - age / (life * (0.55 + 0.45 * hash(i)))) ** 2;
        const dist = HALF_CUBE * 1.1 + this.sparkSpeed[i] * travel;
        pa[i * 3] = d.x * dist; pa[i * 3 + 1] = d.y * dist; pa[i * 3 + 2] = d.z * dist;
        pc[i * 3] = 0.65 * fade; pc[i * 3 + 1] = 0.88 * fade; pc[i * 3 + 2] = 1.1 * fade;
      });
      sp.needsUpdate = true;
      sc.needsUpdate = true;
    }
  }

  private handleLost = (e: Event) => {
    e.preventDefault();
    this.onLost?.();
  };

  dispose() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("pointermove", this.onPtr);
    window.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onUp);
    if (this.cursorSet) document.documentElement.style.cursor = "";
    document.documentElement.removeEventListener("mouseleave", this.offPtr);
    window.removeEventListener("blur", this.offPtr);
    this.ro.disconnect();
    this.io.disconnect();
    this.renderer.domElement.removeEventListener("webglcontextlost", this.handleLost);
    this.meshes[0]?.geometry.dispose();
    this.mats.forEach((m) => m.dispose());
    this.meshes.forEach((m) => m.dispose());
    this.coreInnerMat.dispose();
    this.coreInner.dispose();
    this.bolts.geometry.dispose();
    (this.bolts.material as ShaderMaterial).dispose();
    this.zapSparks.geometry.dispose();
    (this.zapSparks.material as PointsMaterial).dispose();
    this.flashes.forEach((f) => (f.material as SpriteMaterial).dispose());
    this.seams.geometry.dispose();
    (this.seams.material as LineBasicMaterial).dispose();
    this.sparks.geometry.dispose();
    (this.sparks.material as PointsMaterial).dispose();
    [this.halo, ...this.rings, ...this.coreGlow].forEach((sp) => (sp.material as SpriteMaterial).dispose());
    this.glowTex.dispose();
    this.ringTex.dispose();
    this.brushTex.dispose();
    this.env.dispose();
    this.pmrem.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}

export function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return Boolean(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}